import createCache, { type EmotionCache } from '@emotion/cache';

/**
 * REV-803: the per-response CSP nonce.
 *
 * nginx replaces the placeholder in index.html's <meta name="csp-nonce"> with
 * a fresh value on every response and puts the same value in the
 * Content-Security-Policy header. The policy allows inline <style> only with
 * that nonce. (In the Vite dev server nothing replaces it and no CSP is sent,
 * so the placeholder is harmless there.)
 *
 * @returns the nonce, or undefined when the page carries none
 */
export function readCspNonce(doc: Document = document): string | undefined {
  const value = doc.querySelector('meta[name="csp-nonce"]')?.getAttribute('content');
  return value || undefined;
}

/** A CSP nonce is a base64 / base64url token; nothing else is written into markup. */
const NONCE_TOKEN = /^[A-Za-z0-9+/_=-]+$/;

/**
 * Stamp this page's nonce on the <style> elements of an HTML export before it
 * is opened as a blob: URL.
 *
 * A blob: document inherits the Content-Security-Policy of the page that made
 * the blob, so under the enforced policy the printable reports from
 * revenant-api (one <style> in <head>, no script) render completely unstyled:
 * measured in Chromium against the production build. Only <style> gets the
 * nonce. Scripts are left alone on purpose, so `script-src 'self'` still
 * blocks any inline script in the exported document.
 *
 * A string rewrite of the start tags, not DOMParser: a DOMParser document
 * inherits this page's policy too, so parsing the export there raised a CSP
 * violation (and a report to the API) for its un-nonced <style> on every
 * export. Measured in Chromium; jsdom does not show it.
 *
 * @param html  the export document as revenant-api sent it
 * @param nonce this page's nonce; without a valid one the document is returned as-is
 * @returns the document with `nonce` on every <style> start tag
 */
export function nonceExportStyles(
  html: string,
  nonce: string | undefined = readCspNonce()
): string {
  if (!nonce || !NONCE_TOKEN.test(nonce)) {
    return html;
  }
  return html.replace(/<style(?=[\s>])/gi, `<style nonce="${nonce}"`);
}

/**
 * The emotion cache MUI renders through, with the nonce stamped on every
 * <style> element it injects.
 *
 * This is the one exception the policy makes, and why: MUI's styling engine
 * (emotion) writes its CSS into <style> elements at runtime, so they cannot be
 * served as files. Measured by running the e2e suite against the production
 * build under a report-only policy, those <style> elements were the only thing
 * in the app the strict policy blocked.
 */
export function createNoncedEmotionCache(): EmotionCache {
  // Same options as emotion's own default cache (key 'css', appended to
  // <head>), plus the nonce — so the cascade order MUI already relied on does
  // not change.
  return createCache({ key: 'css', nonce: readCspNonce() });
}
